import type { Role } from '@sistemas/contracts'
import {
  Activity,
  Banknote,
  BarChart3,
  Boxes,
  CalendarClock,
  CalendarDays,
  FileBarChart2,
  FolderKanban,
  Gauge,
  GitPullRequest,
  Inbox,
  LayoutDashboard,
  ListChecks,
  Network,
  NotebookPen,
  Plug,
  ScrollText,
  Trophy,
  Users,
  WalletCards,
  Workflow,
  type LucideIcon,
} from 'lucide-react'

/**
 * Navegação do Hub — cobertura completa das 11 capacidades do SDD.
 *
 * REGRA (Q53): item sem permissão fica OCULTO, não desabilitado.
 * Mostrar um item que a pessoa não pode abrir é ruído, não descoberta.
 *
 * Esconder na UI é conveniência; a barreira real é o RLS (I6).
 *
 * Mapa capacidade → item (docs/SDD/00-visao-e-escopo.md §5):
 *   C1  Pessoas & Organização ....... Pessoas, Organograma
 *   C2  Projetos & Kanban ........... Projetos, Minhas atividades, Carga do time, Processos
 *   C3  Chat de Projeto ............. (sem item — painel dentro do projeto)
 *   C4  Chamados & Triagem .......... Chamados
 *   C5  Rotinas & Agenda ............ Rotinas, Agenda
 *   C6  DevOps & GitHub ............. GitHub
 *   C7  Monitoramento ............... Monitoramento
 *   C8  Indicadores & SGI ........... Indicadores
 *   C9  Gamificação ................. GOLD
 *   C10 Budget & Custos ............. Budget, Lançamentos, Relatórios
 *   C11 Caderno ..................... Caderno
 *   —   Transversal ................. Integrações, Auditoria
 */

export interface NavItem {
  readonly to: string
  readonly label: string
  readonly icon: LucideIcon
  /** Papéis que enxergam o item. Ausente = visível a qualquer autenticado. */
  readonly requiresAnyRole?: readonly Role[]
}

export interface NavGroup {
  /** Chave estável — é por ela que a sidebar sabe qual grupo está aberto. */
  readonly id: string
  readonly label: string
  /**
   * Grupo fixo: sem cabeçalho e sem retrair. Só o Dashboard mora aqui — é a
   * porta de entrada de todo mundo e não pode ficar escondido atrás de um clique.
   */
  readonly pinned?: boolean
  readonly items: readonly NavItem[]
}

const GESTAO: readonly Role[] = ['platform_admin', 'gestor', 'lider']
const ADMIN: readonly Role[] = ['platform_admin', 'gestor']
/** Quem trabalha no departamento — fora ficam `leitor` e os papéis laterais sozinhos. */
const TIME: readonly Role[] = ['platform_admin', 'gestor', 'lider', 'membro']
/** ADR-0006 — budget tem chave própria; custo de pessoas não está aqui. */
const BUDGET: readonly Role[] = ['finance_admin', 'budget_operator']

/**
 * Grupos pela pergunta que a pessoa traz, não pela capacidade do SDD:
 * "o que eu faço" (Trabalho), "quem e quando" (Time), "como fomos"
 * (Resultados), "o que está no ar" (Tecnologia), "quanto custa" (Financeiro).
 * A sidebar retrai cada grupo e mantém aberto só o da página atual.
 */
export const NAV_GROUPS: readonly NavGroup[] = [
  {
    id: 'inicio',
    label: 'Início',
    pinned: true,
    items: [{ to: '/dashboard', label: 'Dashboard', icon: LayoutDashboard }],
  },
  {
    id: 'trabalho',
    label: 'Trabalho',
    items: [
      { to: '/minhas-atividades', label: 'Minhas atividades', icon: ListChecks },
      { to: '/projetos', label: 'Projetos', icon: FolderKanban },
      // Todo o time registra chamado (ADR-0015); só a gestão tria, na própria tela.
      { to: '/chamados', label: 'Chamados', icon: Inbox, requiresAnyRole: TIME },
      // C11 — todo o time tem caderno (ADR-0008); cada um vê só o seu.
      { to: '/caderno', label: 'Caderno', icon: NotebookPen },
      // C3 (Chat) NÃO tem item aqui por desenho: é um painel lateral dentro da
      // tela do projeto, não uma tela própria. Ver docs/SDD/07 §2.
    ],
  },
  {
    id: 'time',
    label: 'Time',
    items: [
      { to: '/time', label: 'Pessoas', icon: Users },
      { to: '/organograma', label: 'Organograma', icon: Network },
      // C2 — visão do gestor sobre carga e prazos por pessoa (CU-06.4).
      { to: '/carga-do-time', label: 'Carga do time', icon: Gauge, requiresAnyRole: GESTAO },
      { to: '/rotinas', label: 'Rotinas', icon: CalendarClock },
      { to: '/agenda', label: 'Agenda', icon: CalendarDays },
    ],
  },
  {
    id: 'resultados',
    label: 'Resultados',
    items: [
      { to: '/indicadores', label: 'Indicadores', icon: BarChart3 },
      // C9 — saldo e extrato próprios; ranking nominal não é público (Q47).
      { to: '/gold', label: 'GOLD', icon: Trophy },
    ],
  },
  {
    id: 'tecnologia',
    label: 'Tecnologia',
    items: [
      { to: '/devops', label: 'GitHub', icon: GitPullRequest },
      // Uma tela só: saúde de hosts, de serviços e das fronteiras de
      // integração, com o feed de alertas e a ação de virar chamado ali mesmo.
      { to: '/monitoramento', label: 'Monitoramento', icon: Activity },
      // O catálogo (ADR-0018): liga chamado, log, projeto e MCP ao sistema.
      { to: '/sistemas', label: 'Sistemas', icon: Boxes },
    ],
  },
  {
    // Q51 — o financeiro exige concessão explícita. platform_admin e gestor NÃO
    // bastam (I7). ADR-0006: o budget aceita também `budget_operator`, o papel
    // de quem lança recibo sem alcançar custo de pessoas — que continua fora
    // deste grupo, na tela de Pessoas, atrás de `finance_admin`.
    id: 'financeiro',
    label: 'Financeiro',
    items: [
      {
        to: '/financeiro/budget',
        label: 'Budget',
        icon: Banknote,
        requiresAnyRole: BUDGET,
      },
      {
        to: '/financeiro/lancamentos',
        label: 'Lançamentos',
        icon: WalletCards,
        requiresAnyRole: BUDGET,
      },
      {
        to: '/financeiro/relatorios',
        label: 'Relatórios',
        icon: FileBarChart2,
        requiresAnyRole: BUDGET,
      },
    ],
  },
  {
    // Espelha o grupo "Configuração" do PMC, que também separa admin do operacional.
    id: 'configuracao',
    label: 'Configuração',
    items: [
      // Biblioteca de checklists de processo (docs/SDD/06 §3.6).
      { to: '/processos', label: 'Processos', icon: Workflow, requiresAnyRole: ADMIN },
      {
        to: '/admin/integracoes',
        label: 'Integrações',
        icon: Plug,
        requiresAnyRole: ['platform_admin'],
      },
      {
        to: '/admin/auditoria',
        label: 'Auditoria',
        icon: ScrollText,
        requiresAnyRole: ADMIN,
      },
    ],
  },
]

export function canSeeNavItem(item: NavItem, roles: readonly Role[]): boolean {
  if (!item.requiresAnyRole) return true
  return item.requiresAnyRole.some((required) => roles.includes(required))
}

/** Remove itens sem permissão e grupos que ficaram vazios. */
export function visibleNavGroups(roles: readonly Role[]): NavGroup[] {
  return NAV_GROUPS.map((group) => ({
    ...group,
    items: group.items.filter((item) => canSeeNavItem(item, roles)),
  })).filter((group) => group.items.length > 0)
}

/** A rota atual é o item ou está abaixo dele (`/projetos/abc` pertence a `/projetos`). */
export function matchesNavItem(pathname: string, to: string): boolean {
  return pathname === to || pathname.startsWith(`${to}/`)
}

function findNavEntry(pathname: string): { group: NavGroup; item: NavItem } | null {
  // O item mais específico vence: `/financeiro/budget` antes de um eventual `/financeiro`.
  let best: { group: NavGroup; item: NavItem } | null = null
  for (const group of NAV_GROUPS) {
    for (const item of group.items) {
      if (!matchesNavItem(pathname, item.to)) continue
      if (!best || item.to.length > best.item.to.length) best = { group, item }
    }
  }
  return best
}

/**
 * Grupo da rota atual — o único que a sidebar abre sozinha. `null` fora do
 * catálogo (404), e aí nenhum grupo abre.
 */
export function groupForPath(pathname: string): string | null {
  return findNavEntry(pathname)?.group.id ?? null
}

/** Rota do item da sidebar que responde pela página atual (o mais específico). */
export function navItemForPath(pathname: string): string | null {
  return findNavEntry(pathname)?.item.to ?? null
}

export const NOT_FOUND_LABEL = 'Página não encontrada'

/** Rótulo da rota atual (ou do item pai, para sub-rotas). */
export function routeLabel(pathname: string): string {
  return findNavEntry(pathname)?.item.label ?? NOT_FOUND_LABEL
}

/**
 * Trilha da topbar. Rota de dois níveis (`/admin/auditoria`) mostra o grupo
 * antes do item; rota simples mostra só o item; sub-rota (`/projetos/:id`)
 * mostra o item pai — quem chama acrescenta o nome do registro. Rota
 * desconhecida diz que não existe, em vez de fingir que é o Dashboard.
 */
/** Rotas fora da sidebar (chega-se por outro atalho, como o sino). */
const EXTRA_TRAILS: ReadonlyArray<readonly [prefix: string, trail: string[]]> = [
  ['/notificacoes', ['Notificações']],
]

export function breadcrumbs(pathname: string): string[] {
  const extra = EXTRA_TRAILS.find(([prefix]) => pathname === prefix || pathname.startsWith(`${prefix}/`))
  if (extra) return extra[1]
  const entry = findNavEntry(pathname)
  if (!entry) return [NOT_FOUND_LABEL]
  return entry.item.to.split('/').filter(Boolean).length > 1
    ? [entry.group.label, entry.item.label]
    : [entry.item.label]
}
